import { Trash2, X, AlertTriangle } from 'lucide-react';
import type { MaterialItem } from '../types';

interface StoreDeleteDialogProps {
    isOpen: boolean;
    material: MaterialItem | null;
    onConfirm: () => void;
    onCancel: () => void;
    isDeleting?: boolean;
}

export function StoreDeleteDialog({ isOpen, material, onConfirm, onCancel, isDeleting }: StoreDeleteDialogProps) {
    if (!isOpen || !material) return null;

    const images = Array.isArray(material.images) ? material.images : [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={onCancel}
            />

            {/* Dialog */}
            <div className="relative z-10 w-full max-w-md rounded-2xl border border-red-500/20 bg-card shadow-2xl animate-in zoom-in-95 fade-in duration-200">
                {/* Header */}
                <div className="flex items-center gap-3 p-5 border-b border-border/50">
                    <div className="p-2 rounded-xl bg-red-500/10">
                        <AlertTriangle size={18} className="text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground">Eliminar Material</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Esta acción no se puede deshacer
                        </p>
                    </div>
                    <button
                        onClick={onCancel}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    {/* Material Preview */}
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                        {images.length > 0 ? (
                            <img
                                src={images[0]}
                                alt=""
                                className="w-12 h-12 rounded-lg object-cover border border-border/50"
                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-lg bg-muted/50 flex items-center justify-center border border-border/50">
                                <Trash2 size={16} className="text-muted-foreground/40" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{material.title}</p>
                            <p className="text-xs text-muted-foreground">
                                {material.comments_count} comentario{material.comments_count !== 1 ? 's' : ''}
                                {' · '}
                                {material.ratings_count} valoración{material.ratings_count !== 1 ? 'es' : ''}
                            </p>
                        </div>
                    </div>

                    <p className="text-sm text-muted-foreground">
                        Se eliminarán permanentemente el material, todos sus <strong className="text-foreground">{material.comments_count} comentarios</strong> y <strong className="text-foreground">{material.ratings_count} valoraciones</strong>.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-5 pt-0">
                    <button
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors disabled:opacity-50"
                    >
                        <Trash2 size={14} />
                        {isDeleting ? 'Eliminando...' : 'Eliminar Permanentemente'}
                    </button>
                </div>
            </div>
        </div>
    );
}
